import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useThunk } from "../../hooks/useThunk";
import { getCustomerById } from "../../services/thunks/customerThunks";
import showToast from "../../utils/toastNotifications";

// Import icons
import backIcon from "../../assets/icons/back.svg"; // Add this icon to your assets
import Input from "../../components/Form/Input";
import { formStyles } from "../../components/Form/FormUtils";

const CustomerViewDetails = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const [doGetCustomer] = useThunk(getCustomerById);

  useEffect(() => {
    fetchCustomerDetails();
  }, [customerId]);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      const result = await doGetCustomer({ id: customerId });

      if (result.success) {
        setCustomer(result.response.data);
      } else {
        showToast(
          "error",
          result.error?.message || "Failed to fetch customer details"
        );
      }
    } catch (error) {
      showToast("error", "An error occurred while fetching customer details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  if (loading) {
    return (
      <div className="px-6 py-10 text-white min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading customer details...</div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="px-6 py-10 text-white min-h-screen">
        <div className="flex items-center mb-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-[#51F9EF] hover:text-white transition-colors"
          >
            <img src={backIcon} alt="back" className="w-4 h-4" />
            Back
          </button>
        </div>
        <div className="text-center text-red-500">
          Customer not found or failed to load details.
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-10 text-white min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-[#FEFEFE] text-[16px] font-semibold font-public-sans "
          >
            <img src={backIcon} alt="back" className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>

      {/* Customer Details Form View */}
      <div className="bg-[#111111D6] rounded-lg p-5 border border-[#505050]">
        <h1 className="text-[20px] font-bold font-public-sans mb-[10px]">
          Application Details
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-[20px] gap-y-[10px] ">
          {/* First Name */}
          <div>
            <Input
              label="First Name"
              value={
                customer.firstName || customer.name?.split(" ")[0] || "N/A"
              }
              readOnly={true}
              inputStyle={formStyles.customerApplicationInput}
              labelStyle={formStyles.customerApplicationlabel}
              className="cursor-default"
            />
          </div>

          {/* Last Name */}
          <div>
            <Input
              label="Last Name"
              value={
                customer.lastName ||
                customer.name?.split(" ").slice(1).join(" ") ||
                "N/A"
              }
              readOnly={true}
              inputStyle={formStyles.customerApplicationInput}
              labelStyle={formStyles.customerApplicationlabel}
              className="cursor-default"
            />
          </div>

          {/* Date Submitted */}
          <div>
            <Input
              label="Date Submitted"
              value={formatDate(customer.createdAt || customer.joinedOn)}
              readOnly={true}
              inputStyle={formStyles.customerApplicationInput}
              labelStyle={formStyles.customerApplicationlabel}
              className="cursor-default"
            />
          </div>

          {/* Email Address */}
          <div>
            <Input
              label="Email Address"
              type="email"
              value={customer.email || "N/A"}
              readOnly={true}
              inputStyle={formStyles.customerApplicationInput}
              labelStyle={formStyles.customerApplicationlabel}
              className="cursor-default"
            />
          </div>


          {/* DJ Name */}
          <div>
            <Input
              label="DJ Name"
              value={customer.djName || customer.stageName || "N/A"}
              readOnly={true}
              inputStyle={formStyles.customerApplicationInput}
              labelStyle={formStyles.customerApplicationlabel}
              className="cursor-default"
            />
          </div>

          {/* Type of DJ */}
          <div>
            <Input
              label="Type of DJ"
              value={customer.djType || customer.typeOfDj || "N/A"}
              readOnly={true}
              inputStyle={formStyles.customerApplicationInput}
              labelStyle={formStyles.customerApplicationlabel}
              className="cursor-default"
            />
          </div>

          {/* Country */}
          <div>
            <Input
              label="Country"
              value={customer.country || "N/A"}
              readOnly={true}
              inputStyle={formStyles.customerApplicationInput}
              labelStyle={formStyles.customerApplicationlabel}
              className="cursor-default"
            />
          </div>

          {/* State / Province */}
          <div>
            <Input
              label="State / Province"
              value={customer.state || customer.province || "N/A"}
              readOnly={true}
              inputStyle={formStyles.customerApplicationInput}
              labelStyle={formStyles.customerApplicationlabel}
              className="cursor-default"
            />
          </div>

          {/* Website */}
          <div>
            <Input
              label="Website (Optional)"
              type="url"
              value={customer.website || "N/A"}
              readOnly={true}
              inputStyle={formStyles.customerApplicationInput}
              labelStyle={formStyles.customerApplicationlabel}
              onClick={() => {
                if (customer.website) {
                  const url = customer.website.startsWith("http")
                    ? customer.website
                    : `https://${customer.website}`;
                  window.open(url, "_blank", "noopener,noreferrer");
                }
              }}
              className="cursor-pointer"
            />
          </div>

          {/* SoundCloud / Mixcloud */}
          <div>
            <Input
              label="SoundCloud/Mixcloud (Optional)"
              type="url"
              value={customer.soundcloud || customer.mixcloud || "N/A"}
              readOnly={true}
              inputStyle={formStyles.customerApplicationInput}
              labelStyle={formStyles.customerApplicationlabel}
              onClick={() => {
                const url = customer.soundcloud || customer.mixcloud;
                if (url) {
                  window.open(url, "_blank", "noopener,noreferrer");
                }
              }}
              className="cursor-pointer"
            />
          </div>

          {/* Instagram Handle */}
          <div>
            <Input
              label="Instagram Handle(Optional)"
              value={
                customer.instagram
                  ? customer.instagram.startsWith("@")
                    ? customer.instagram
                    : `@${customer.instagram}`
                  : "N/A"
              }
              readOnly={true}
              inputStyle={formStyles.customerApplicationInput}
              labelStyle={formStyles.customerApplicationlabel}
              onClick={() => {
                if (customer.instagram) {
                  const handle = customer.instagram.replace("@", "");
                  window.open(
                    `https://instagram.com/${handle}`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }
              }}
              className="cursor-pointer"
            />
          </div>

          {/* Facebook Handle */}
          <div>
            <Input
              label="Facebook Handle(Optional)"
              value={
                customer.facebook
                  ? customer.facebook.startsWith("@")
                    ? customer.facebook
                    : `@${customer.facebook}`
                  : "N/A"
              }
              readOnly={true}
              inputStyle={formStyles.customerApplicationInput}
              labelStyle={formStyles.customerApplicationlabel}
              onClick={() => {
                if (customer.facebook) {
                  const handle = customer.facebook.replace("@", "");

                  window.open(
                    `https://facebook.com/${handle}`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }
              }}
              className="cursor-pointer"
            />
          </div>

          {/* Additional Info */}
          <div>
            <Input
              label="Additional Information"
              value={
                customer.additionalInfo ||
                customer.notes ||
                customer.bio ||
                "N/A"
              }
              readOnly={true}
              rows={6}
              inputStyle={formStyles.customerApplicationInput}
              labelStyle={formStyles.customerApplicationlabel}
              className="cursor-default"
            />
          </div>

          {/*Referred By*/}
          <div>
            <Input
              label="Referred By"
              value={customer.referredBy || "N/A"}
              readOnly={true}
              inputStyle={formStyles.customerApplicationInput}
              labelStyle={formStyles.customerApplicationlabel}
              className="cursor-default"
            />
        </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerViewDetails;
